import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const ISSUE_META = {
  Cancellation: { label: 'Cancellation', color: '#b33b2f', bg: '#fdecea' },
  Alteration: { label: 'Alteration', color: '#6A7079', bg: '#F7F8F9' },
  default: { label: 'Support', color: '#1d4f91', bg: '#e9f0fa' },
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

const toDate = (timestamp) => {
  if (!timestamp) return null;
  try {
    return timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  } catch {
    return null;
  }
};

const formatCreatedAt = (timestamp) => {
  const date = toDate(timestamp);
  if (!date) return 'Just now';
  return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const normalizePhone = (phone) => String(phone || '').replace(/\D/g, '');

const getInitials = (name, email) => {
  const src = (name || email || 'U').trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

function StatCard({ label, value, tone }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2d7c6', borderRadius: 4, padding: '11px 12px', minWidth: 0 }}>
      <div style={{ fontSize: '1.18rem', fontWeight: 900, color: tone, lineHeight: 1 }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: '0.64rem', color: '#6A7079', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    </div>
  );
}

const chipStyle = {
  fontSize: '0.73rem',
  color: '#67543b',
  background: '#F7F8F9',
  border: '1px solid #D7DBDF',
  borderRadius: 3,
  padding: '4px 9px',
  maxWidth: 280,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const actionButtonStyle = {
  background: '#15100a',
  color: '#fff',
  border: '1px solid #15100a',
  borderRadius: 4,
  padding: '8px 12px',
  fontSize: '0.74rem',
  fontWeight: 900,
  cursor: 'pointer',
  textDecoration: 'none',
  height: 35,
  display: 'inline-flex',
  alignItems: 'center',
};

const secondaryButtonStyle = {
  background: '#faf7f1',
  border: '1px solid #D7DBDF',
  borderRadius: 4,
  padding: '8px 11px',
  cursor: 'pointer',
  color: '#80643d',
  fontSize: '0.74rem',
  fontWeight: 900,
  textDecoration: 'none',
  height: 35,
  display: 'inline-flex',
  alignItems: 'center',
};

const dangerButtonStyle = {
  ...secondaryButtonStyle,
  background: '#fff',
  border: '1px solid #f0cfca',
  color: '#b33b2f',
};

function SupportAvatar({ item }) {
  const [errored, setErrored] = useState(false);
  const showImg = item.userPhoto && !errored;
  return (
    <div style={{ width: 44, height: 44, borderRadius: 4, overflow: 'hidden', background: '#17110a', color: '#d8b36a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.9rem', flexShrink: 0 }}>
      {showImg ? (
        <img src={item.userPhoto} alt="" onError={() => setErrored(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        getInitials(item.name, item.email)
      )}
    </div>
  );
}

function SupportCard({ item, onStatusChange, onDelete }) {
  const phoneDigits = normalizePhone(item.phone);
  const phoneForWhatsApp = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits;
  const status = item.status || 'pending';
  const statusColor = status === 'resolved' ? '#187044' : status === 'closed' ? '#7a6750' : status === 'in_progress' ? '#1d4f91' : '#b45309';
  const issue = ISSUE_META[item.issueType] || ISSUE_META.default;

  return (
    <article style={{ background: '#fff', border: '1px solid #D7DBDF', borderRadius: 4, padding: 16, boxShadow: '0 1px 2px rgba(31,22,10,0.04)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr) auto', gap: 14, alignItems: 'start' }}>
        <SupportAvatar item={item} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 7 }}>
            <span style={{ padding: '3px 9px', borderRadius: 3, background: issue.bg, color: issue.color, fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {issue.label}
            </span>
            <span style={{ padding: '3px 8px', borderRadius: 3, background: '#F7F8F9', color: statusColor, fontSize: '0.68rem', fontWeight: 900 }}>
              {STATUS_LABELS[status] || STATUS_LABELS.pending}
            </span>
            <span style={{ fontSize: '0.74rem', color: '#6A7079', fontWeight: 650 }}>{formatCreatedAt(item.createdAt)}</span>
          </div>

          <h3 style={{ margin: '0 0 7px', color: '#17110a', fontSize: '1rem', fontWeight: 900, lineHeight: 1.25 }}>
            {item.name || 'Customer'}
          </h3>

          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
            {item.orderId && <span style={chipStyle}><b>Order:</b> #{item.orderId}</span>}
            {item.productName && <span style={chipStyle}><b>Product:</b> {item.productName}</span>}
            {item.email && <span style={chipStyle}><b>Email:</b> {item.email}</span>}
            {item.phone && <span style={chipStyle}><b>Phone:</b> {item.phone}</span>}
          </div>

          {item.message && (
            <p style={{ margin: 0, color: '#5e4d39', fontSize: '0.84rem', lineHeight: 1.55, background: '#F7F8F9', border: '1px solid #D7DBDF', borderRadius: 4, padding: '9px 11px' }}>
              {item.message}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', minWidth: 300 }}>
          {phoneDigits && (
            <>
              <a href={`tel:+${phoneForWhatsApp}`} style={actionButtonStyle}>Call</a>
              <a href={`https://wa.me/${phoneForWhatsApp}`} target="_blank" rel="noreferrer" style={secondaryButtonStyle}>WhatsApp</a>
            </>
          )}
          {item.email && <a href={`mailto:${item.email}`} style={secondaryButtonStyle}>Email</a>}
          {status !== 'in_progress' && <button type="button" onClick={() => onStatusChange(item.id, 'in_progress')} style={secondaryButtonStyle}>In Progress</button>}
          {status !== 'resolved' && <button type="button" onClick={() => onStatusChange(item.id, 'resolved')} style={secondaryButtonStyle}>Resolve</button>}
          <button type="button" onClick={() => onDelete(item.id)} style={dangerButtonStyle}>Delete</button>
        </div>
      </div>
    </article>
  );
}

function SupportRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const unsubscribe = db.collection('SupportRequests')
      .orderBy('createdAt', 'desc')
      .limit(200)
      .onSnapshot(
        (snapshot) => {
          setItems(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching support requests:', error);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const status = item.status || 'pending';
      if (filter !== 'all' && status !== filter) return false;
      if (!query) return true;
      return [item.name, item.email, item.phone, item.orderId, item.productName, item.message, item.issueType]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [items, filter, search]);

  const counts = useMemo(() => {
    return items.reduce((summary, item) => {
      const status = item.status || 'pending';
      summary.all += 1;
      summary[status] = (summary[status] || 0) + 1;
      return summary;
    }, { all: 0, pending: 0, in_progress: 0, resolved: 0, closed: 0 });
  }, [items]);

  const handleStatusChange = async (id, status) => {
    try {
      await db.collection('SupportRequests').doc(id).update({ status, updatedAt: new Date() });
    } catch (error) {
      console.error('Error updating support request:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await db.collection('SupportRequests').doc(id).delete();
    } catch (error) {
      console.error('Error deleting support request:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6A7079', fontWeight: 800 }}>
        Loading support requests...
      </div>
    );
  }

  return (
    <div style={{ padding: '22px 26px 34px', minHeight: '100vh', background: '#EEF0F2', boxSizing: 'border-box' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(244,241,235,0.96)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #ded4c4', paddingBottom: 14, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#6A7079', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 5 }}>
              MAVIINCI Care
            </div>
            <h2 style={{ margin: 0, color: '#17110a', fontSize: '1.65rem', fontWeight: 900, lineHeight: 1.05 }}>
              Support Requests
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(82px, 1fr))', gap: 8, flex: '0 1 460px' }}>
            <StatCard label="Total" value={counts.all} tone="#17110a" />
            <StatCard label="Pending" value={counts.pending} tone="#b45309" />
            <StatCard label="In Progress" value={counts.in_progress} tone="#1d4f91" />
            <StatCard label="Resolved" value={counts.resolved} tone="#187044" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) auto', gap: 10, alignItems: 'center', marginBottom: 10 }}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, order, email, message..."
            style={{ width: '100%', padding: '11px 13px', borderRadius: 4, border: '1px solid #d8cab5', background: '#fff', fontSize: '0.86rem', color: '#2c1e0a', outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 6, background: '#fff', border: '1px solid #d8cab5', borderRadius: 4, padding: 5, overflowX: 'auto' }}>
            {FILTERS.map((item) => {
              const active = filter === item.key;
              return (
                <button key={item.key} type="button" onClick={() => setFilter(item.key)} style={{ background: active ? '#17110a' : 'transparent', color: active ? '#fff' : '#6f604e', border: 'none', borderRadius: 9, padding: '8px 11px', fontSize: '0.74rem', fontWeight: 850, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                  {item.label}
                  <span style={{ background: active ? 'rgba(255,255,255,0.2)' : '#e8ddd0', color: active ? '#fff' : '#6A7079', borderRadius: 3, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 800, minWidth: 20, textAlign: 'center' }}>{counts[item.key] || 0}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ fontSize: '0.86rem', color: '#6f604e', fontWeight: 800, marginBottom: 12 }}>
        {filteredItems.length} visible
      </div>

      {filteredItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 20px', background: '#fff', borderRadius: 4, border: '1px solid #D7DBDF' }}>
          <h4 style={{ margin: '0 0 8px', fontWeight: 800, color: '#14110d', fontSize: '1.1rem' }}>No support requests found</h4>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#8c7558' }}>
            Cancellation and alteration requests will appear here in real time.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredItems.map((item) => (
            <SupportCard
              key={item.id}
              item={item}
              onStatusChange={handleStatusChange}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default SupportRequestsPage;
